/**
 * TUVI - Paz y Salvo
 * Genera el documento de Paz y Salvo del proyecto desde plantilla Word
 * (misma mecánica que el Acta de Entrega: plantilla + datos → PDF).
 */
import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { UPLOAD_URL } from '../../../config';
import './ActaEntrega.css';

export default function PazYSalvo({ proyecto }) {
  const { success, error } = useNotification();
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [generado, setGenerado] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [docKey, setDocKey] = useState(null);

  useEffect(() => {
    const verificarExistente = async () => {
      if (!proyecto?.proyecto) return;
      const p = proyecto.proyecto;
      const nombre = p.proyecto_nombre || 'proyecto';
      const apartamento = p.apartamento || 'apto';
      try {
        const res = await fetch(`${UPLOAD_URL}/paz-y-salvo/check/${encodeURIComponent(nombre)}/${encodeURIComponent(apartamento)}`);
        const data = await res.json();
        if (data.success && data.exists && data.key) {
          setDocKey(data.key);
          setGenerado(true);
        }
      } catch (err) {
        console.error('Error verificando paz y salvo:', err);
      }
    };
    verificarExistente();
  }, [proyecto]);

  if (!proyecto) return null;

  const { proyecto: p, consideraciones } = proyecto;
  const info = p?.infoAdicional || consideraciones || {};
  const cliente = p?.nombre_cliente || '';
  const cedula = p?.cedula || info.cedula_nit || '';
  const proyNombre = p?.proyecto_nombre || '';
  const apto = p?.apartamento || '';
  const valorFinal = parseFloat(p?.valor_final) || 0;

  const formatMoney = (v) => new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: 'COP', minimumFractionDigits: 0,
  }).format(v || 0);

  const handleGenerar = async () => {
    setGenerando(true);
    try {
      const fd = new FormData();
      fd.append('proyecto', proyNombre);
      fd.append('apartamento', apto);
      fd.append('propietario', cliente);
      fd.append('cedula', cedula);
      fd.append('fecha', fecha);
      fd.append('valor', String(valorFinal));

      const token = localStorage.getItem('tuvi_token');
      const res = await fetch(`${UPLOAD_URL}/paz-y-salvo/generar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const result = await res.json();
      if (result.success) {
        setDocKey(result.key);
        setGenerado(true);
        success(result.message || 'Paz y Salvo generado');
      } else {
        error(result.message);
      }
    } catch (err) {
      console.error(err);
      error('Error generando el Paz y Salvo');
    } finally {
      setGenerando(false);
    }
  };

  const handleDescargar = async () => {
    if (!docKey) return;
    try {
      const res = await fetch(`${UPLOAD_URL}/paz-y-salvo/${encodeURIComponent(docKey)}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = docKey.endsWith('.pdf') ? 'pdf' : 'docx';
      a.download = `Paz_y_Salvo_${proyNombre}_${apto}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      error('Error descargando el Paz y Salvo');
    }
  };

  const ext = docKey?.endsWith('.pdf') ? 'PDF' : 'Word';

  return (
    <div className="acta-container">
      <div className="acta-panel-form" style={{ maxWidth: 500, margin: '0 auto' }}>
        <h3 className="acta-form-title">Paz y Salvo</h3>
        <p className="acta-form-subtitle">Certifica que el propietario canceló la totalidad del contrato</p>

        <div className="acta-info-card">
          <div className="acta-info-row"><span className="acta-info-label">Proyecto:</span><span>{proyNombre}</span></div>
          <div className="acta-info-row"><span className="acta-info-label">Apartamento:</span><span>{apto}</span></div>
          <div className="acta-info-row"><span className="acta-info-label">Propietario:</span><span>{cliente}</span></div>
          <div className="acta-info-row"><span className="acta-info-label">Cedula:</span><span>{cedula || 'No registrada'}</span></div>
          <div className="acta-info-row"><span className="acta-info-label">Valor del Contrato:</span><span>{formatMoney(valorFinal)}</span></div>
        </div>

        <div className="acta-form-field">
          <label>Fecha del Documento *</label>
          <input type="date" value={fecha} onChange={e => { setFecha(e.target.value); setGenerado(false); }} />
        </div>

        <div className="acta-form-actions">
          <button className="acta-btn acta-btn-generar" onClick={handleGenerar} disabled={generando || !fecha || !valorFinal}>
            {generando ? 'Generando...' : generado ? 'Regenerar Paz y Salvo' : 'Generar Paz y Salvo'}
          </button>
          {generado && docKey && (
            <button className="acta-btn acta-btn-descargar" onClick={handleDescargar}>
              Descargar {ext}
            </button>
          )}
        </div>
        {!valorFinal && (
          <p className="acta-form-subtitle" style={{ color: '#dc2626', marginTop: 8 }}>
            El proyecto no tiene valor final registrado — se requiere para el documento.
          </p>
        )}
      </div>
    </div>
  );
}
